import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { ICON_COMPONENTS } from "@/lib/iconChoices";
import { resolveButtonStyle } from "@/lib/ctaButtonStyle";
import CtaButtonBlocks, { ctaButtonBlocks } from "./CtaButtonBlocks";
import RichText from "@/components/RichText";

// Valeurs par défaut identiques à theme_service.DEFAULT_PROPS["image_with_text"]
// — voir HeroSection.jsx pour l'explication du repli.
const DEFAULTS = {
  heading: "Un titre qui accroche",
  body: "",
  image_url: "",
  image_position: "left",
  cta_label: "",
  cta_url: "",
  variant: "light",
  // Optionnels (Sprint 12) — voir DEFAULT_PROPS.image_with_text (backend).
  eyebrow: "",
  checklist: "",
  badge_icon: "",
  badge_title: "",
  badge_subtitle: "",
  anchor_id: "",
  // Optionnels (Sprint 13) — voir DEFAULT_PROPS.image_with_text (backend).
  checklist_style: "list",
  footnote: "",
};

export default function ImageWithTextSection({ props = {}, blocks }) {
  const t = { ...DEFAULTS, ...props };
  const imageFirst = t.image_position !== "right";
  const dark = t.variant === "dark";
  const checklist = (t.checklist || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const chips = t.checklist_style === "chips";
  // Filtre par TYPE de bloc : cette section accepte désormais aussi des
  // blocs "cta_button" (boutons supplémentaires, voir CtaButtonBlocks.jsx),
  // qui ne sont pas des fiches profil.
  const cards = (blocks || []).filter((b) => b.type === "profile_card" && b.enabled !== false && b.props?.title);
  const extraButtons = ctaButtonBlocks(blocks);
  const BadgeIcon = t.badge_icon ? ICON_COMPONENTS[t.badge_icon] : null;
  const [imgError, setImgError] = useState(false);
  const ctaStyle = resolveButtonStyle(t, "cta");

  return (
    <section id={t.anchor_id || undefined} className={`scroll-mt-28 ${dark ? "bg-ink" : ""}`}>
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-section md:py-section-md">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className={`relative ${imageFirst ? "order-1" : "order-2"}`}>
            {t.image_url && !imgError ? (
              <img
                src={t.image_url}
                alt=""
                className="w-full rounded-2xl object-cover aspect-[4/3]"
                onError={() => setImgError(true)}
              />
            ) : (
              <div className={`w-full rounded-2xl aspect-[4/3] ${dark ? "bg-white/10" : "bg-sand"}`} />
            )}
            {BadgeIcon && t.badge_title && (
              <div className="absolute top-5 left-5 flex items-center gap-3 bg-ink/90 backdrop-blur-sm text-white rounded-xl px-4 py-3 shadow-lg max-w-[80%]">
                <span className="h-9 w-9 rounded-lg bg-gold/20 flex items-center justify-center shrink-0">
                  <BadgeIcon className="h-5 w-5 text-gold" />
                </span>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold leading-tight truncate">{t.badge_title}</p>
                  {t.badge_subtitle && <p className="text-[11.5px] text-white/60 leading-tight truncate">{t.badge_subtitle}</p>}
                </div>
              </div>
            )}
          </div>
          <div className={imageFirst ? "order-2" : "order-1"}>
            {t.eyebrow && (
              <RichText as="p" className="text-[13px] font-semibold tracking-wide text-gold uppercase mb-3" html={t.eyebrow} />
            )}
            <RichText
              as="h2"
              className={`font-display font-semibold text-[28px] md:text-[34px] leading-tight tracking-[-0.02em] ${dark ? "text-cream" : "text-ink"}`}
              html={t.heading}
            />
            {t.body && (
              <RichText
                as="div"
                preserveParagraphs
                className={`text-[16px] mt-4 leading-relaxed [&_a]:underline [&_a]:text-gold ${dark ? "text-cream/70" : "text-graphite"}`}
                html={t.body}
              />
            )}
            {checklist.length > 0 && (
              chips ? (
                <div className="mt-5 flex flex-wrap gap-2.5">
                  {checklist.map((item, i) => (
                    <span
                      key={i}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[13.5px] font-medium ${
                        dark ? "border-white/15 bg-white/5 text-cream/90" : "border-stone bg-white text-graphite"
                      }`}
                    >
                      <Check className="h-3.5 w-3.5 text-gold shrink-0" strokeWidth={2.4} />
                      {item}
                    </span>
                  ))}
                </div>
              ) : (
                <ul className="mt-5 space-y-2.5">
                  {checklist.map((item, i) => (
                    <li key={i} className={`flex items-start gap-2.5 text-[14.5px] ${dark ? "text-cream/80" : "text-graphite"}`}>
                      <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" strokeWidth={2} />
                      {item}
                    </li>
                  ))}
                </ul>
              )
            )}
            {((t.cta_label && t.cta_url) || extraButtons.length > 0) && (
              <div className="mt-7 flex flex-wrap items-center gap-4">
                {t.cta_label && t.cta_url && (
                  <a
                    href={t.cta_url}
                    data-testid="image-with-text-cta"
                    className={`inline-flex items-center gap-2 px-7 py-3.5 text-[13px] font-semibold uppercase tracking-[0.1em] transition-opacity hover:opacity-90 ${
                      dark ? "bg-gold text-ink" : "bg-ink text-white"
                    } ${ctaStyle.className}`}
                    style={ctaStyle.style}
                    aria-label={!ctaStyle.showText ? t.cta_label : undefined}
                  >
                    {ctaStyle.showIcon && ctaStyle.Icon && <ctaStyle.Icon className="h-4 w-4" />}
                    {ctaStyle.showText && t.cta_label}
                  </a>
                )}
                <CtaButtonBlocks blocks={blocks} tone={dark ? "dark" : "light"} size="caps" />
              </div>
            )}
            {cards.length > 0 && (
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {cards.map((block) => {
                  const ctaLabel = block.props?.cta_label;
                  const ctaUrl = block.props?.cta_url || "#";
                  return (
                    <div
                      key={block.id}
                      data-block-key={block.id}
                      className={`rounded-2xl border p-5 ${dark ? "border-white/10 bg-white/[0.04]" : "border-stone bg-white"}`}
                    >
                      <RichText as="h3" className={`font-display font-semibold text-[15.5px] ${dark ? "text-white" : "text-ink"}`} html={block.props.title} />
                      {block.props?.desc && (
                        <p className={`text-[13.5px] mt-2 leading-relaxed ${dark ? "text-white/60" : "text-graphite"}`}>{block.props.desc}</p>
                      )}
                      {ctaLabel && (
                        ctaUrl.startsWith("/") ? (
                          <Link to={ctaUrl} className="inline-flex items-center gap-1 text-[13px] font-semibold text-gold hover:opacity-80 transition-opacity mt-3">
                            {ctaLabel} <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                        ) : (
                          <a href={ctaUrl} className="inline-flex items-center gap-1 text-[13px] font-semibold text-gold hover:opacity-80 transition-opacity mt-3">
                            {ctaLabel} <ArrowRight className="h-3.5 w-3.5" />
                          </a>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {t.footnote && (
              <p className={`text-[12px] mt-4 ${dark ? "text-white/40" : "text-ash"}`}>{t.footnote}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
